/**
 * DeckPreview — full-screen editable preview of the JLR Member
 * Experience deck. Dipali clicks "Preview" from the CI dashboard, we
 * call Claude in the background to draft all narratives (~30-60s),
 * then this modal opens with every editable section rendered as a
 * card.
 *
 * For each editable narrative:
 *   - Read the Claude draft
 *   - Click the text to edit inline (auto-save on blur)
 *   - Click "Rewrite this" → type an instruction → Claude rewrites
 *   - Click "Reset" to discard the manual edit (reverts to last Claude draft)
 *
 * When every section is read through, click "Generate final PPT" at
 * the bottom → assembles the PPTX using the approved content (NO new
 * Claude calls) and downloads.
 *
 * Design: warm parchment + navy matching the rest of the CI dashboard.
 * Each section gets its own card with slide-index marker and an
 * eyebrow badge for the section type.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  X, RefreshCw, Edit3, Wand2, CheckCircle2, Download, Loader2,
  AlertCircle, ChevronRight,
} from 'lucide-react';
import api from '@/lib/api';

const TYPE_LABELS = {
  cover: 'Cover',
  'overview-numbers': 'Overview · numbers',
  pillar: 'Pillar narrative',
  read: 'Strategic read',
  'proactive-care': 'Proactive Care',
  closing: 'Closing',
};

const TYPE_COLORS = {
  cover: { bg: '#1B2A4A', text: '#FFFFFF' },
  'overview-numbers': { bg: '#E5C9A0', text: '#1B2A4A' },
  pillar: { bg: '#FFF8EE', text: '#8A4B00' },
  read: { bg: '#ECF2FA', text: '#1C304A' },
  'proactive-care': { bg: '#FFF4E3', text: '#8A4B00' },
  closing: { bg: '#1B2A4A', text: '#FFFFFF' },
};

export default function DeckPreview({ clientId, month, start, end, onClose, onFinalized }) {
  const [previewId, setPreviewId] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | drafting | ready | finalized | failed
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busySectionId, setBusySectionId] = useState(null);
  const [showInstructionFor, setShowInstructionFor] = useState(null);
  const [instructionText, setInstructionText] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const pollRef = useRef(null);

  // 1) Kick off the preview when this component mounts
  useEffect(() => {
    let cancelled = false;
    const start_ = async () => {
      try {
        setStatus('drafting');
        setError(null);
        const payload = { month, start, end };
        // Strip undefined so backend `_parse_period` doesn't error
        Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);
        const r = await api.post(
          `/customer-intelligence/${clientId}/deck/preview`,
          null, { params: payload, timeout: 30000 },
        );
        if (cancelled) return;
        setPreviewId(r.data?.preview_id);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message);
        setStatus('failed');
      }
    };
    start_();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line
  }, [clientId]);

  // 2) Poll the preview doc until status=ready
  useEffect(() => {
    if (!previewId) return;
    const poll = async () => {
      try {
        const r = await api.get(
          `/customer-intelligence/${clientId}/deck/preview/${previewId}`,
        );
        const doc = r.data;
        setPreview(doc);
        if (doc?.status === 'ready') {
          setStatus('ready');
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        } else if (doc?.status === 'failed') {
          setStatus('failed');
          setError(doc?.error || 'Drafting failed');
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch (e) {
        // Keep polling on transient errors
        // eslint-disable-next-line no-console
        console.warn('[deck-preview poll]', e?.message);
      }
    };
    // First poll immediately, then every 3s
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [previewId, clientId]);

  const regenerate = async (sectionId, instruction = null) => {
    setBusySectionId(sectionId);
    try {
      const r = await api.post(
        `/customer-intelligence/${clientId}/deck/preview/${previewId}/regenerate-section`,
        { section_id: sectionId, instruction },
        { timeout: 120000 },
      );
      // Splice the updated section back into local state
      setPreview((prev) => {
        if (!prev) return prev;
        const sections = (prev.sections || []).map((s) =>
          s.id === sectionId ? r.data.section : s,
        );
        return { ...prev, sections };
      });
      setShowInstructionFor(null);
      setInstructionText('');
    } catch (e) {
      alert(`Rewrite failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusySectionId(null);
    }
  };

  const saveEdit = async (sectionId, narrative) => {
    try {
      await api.patch(
        `/customer-intelligence/${clientId}/deck/preview/${previewId}/section`,
        { section_id: sectionId, narrative },
      );
    } catch (e) {
      alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      const r = await api.post(
        `/customer-intelligence/${clientId}/deck/preview/${previewId}/finalize`,
        null, { timeout: 180000 },
      );
      const jobId = r.data?.job_id;
      // Trigger the download
      const dl = await api.get(
        `/customer-intelligence/${clientId}/deck/download/${jobId}`,
        { responseType: 'blob', timeout: 60000 },
      );
      const blob = new Blob([dl.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = URL.createObjectURL(blob);
      const slug = preview?.month || `${preview?.start}_to_${preview?.end}`;
      const fname = `${clientId}_${slug}_member_experience.pptx`;
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setStatus('finalized');
      if (onFinalized) onFinalized({ jobId, size: blob.size });
      // Keep the modal open briefly so the user sees the success state
      setTimeout(() => onClose && onClose(), 1500);
    } catch (e) {
      alert(`PPT generation failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFinalizing(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[60] flex flex-col"
         style={{ background: '#F7F5F2' }}
         data-testid="deck-preview-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b"
           style={{ background: '#1B2A4A', borderColor: '#2A3C5C' }}>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
            Preview · editable
          </span>
          <span className="text-white font-medium text-sm">
            {clientId.toUpperCase()} Member Experience Deck
          </span>
          <span className="text-[11px]" style={{ color: '#B7C2D8' }}>
            · {preview?.period || month || `${start} to ${end}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {status === 'ready' && (
            <button
              onClick={finalize}
              disabled={finalizing}
              data-testid="deck-preview-finalize"
              className="text-[12px] px-3 py-1.5 rounded font-medium flex items-center gap-1.5 hover:opacity-90 disabled:opacity-60"
              style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
              {finalizing
                ? <><Loader2 size={13} className="animate-spin" /> Generating PPT…</>
                : <><Download size={13} /> Generate Final PPT</>}
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="deck-preview-close"
            title="Close preview"
            className="text-white hover:opacity-80">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {status === 'drafting' && (
          <div className="max-w-3xl mx-auto py-20 text-center space-y-4"
               data-testid="deck-preview-drafting">
            <Loader2 size={32} className="animate-spin mx-auto" style={{ color: '#1B2A4A' }} />
            <div className="text-[14px]" style={{ color: '#1B2A4A' }}>
              Drafting all narratives — this takes 30-90 seconds.
            </div>
            <div className="text-[11px]" style={{ color: '#6B7280' }}>
              Claude is writing pillar narratives, proactive care, and the strategic read. You'll
              see the full editable preview as soon as it's ready.
            </div>
          </div>
        )}
        {status === 'failed' && (
          <div className="max-w-xl mx-auto py-20 text-center space-y-3"
               data-testid="deck-preview-failed">
            <AlertCircle size={28} className="mx-auto" style={{ color: '#B91C1C' }} />
            <div className="text-[14px]" style={{ color: '#1B2A4A' }}>
              Preview failed to draft.
            </div>
            <div className="text-[12px]" style={{ color: '#6B7280' }}>
              {error || 'Unknown error'}
            </div>
            <button
              onClick={onClose}
              className="text-[12px] px-3 py-1.5 rounded hover:opacity-90"
              style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
              Close
            </button>
          </div>
        )}
        {status === 'ready' && preview && (
          <div className="max-w-4xl mx-auto space-y-3">
            <div className="text-[11px]" style={{ color: '#6B7280' }}>
              {preview.sections?.length || 0} sections · click any narrative to edit, or use
              "Rewrite this" to ask Claude for a new version. Changes auto-save. When you're
              happy, click <strong>Generate Final PPT</strong> above.
            </div>
            {(preview.sections || []).map((section, i) => (
              <DeckPreviewSection
                key={section.id}
                section={section}
                index={i}
                busy={busySectionId === section.id}
                showInstruction={showInstructionFor === section.id}
                instructionText={instructionText}
                setInstructionText={setInstructionText}
                onEditSave={(narrative) => saveEdit(section.id, narrative)}
                onRegenerate={() => regenerate(section.id, null)}
                onRegenerateWithInstruction={() => regenerate(section.id, instructionText)}
                onShowInstruction={() => {
                  setShowInstructionFor(section.id);
                  setInstructionText('');
                }}
                onCancelInstruction={() => {
                  setShowInstructionFor(null); setInstructionText('');
                }}
              />
            ))}
          </div>
        )}
        {status === 'finalized' && (
          <div className="max-w-xl mx-auto py-20 text-center space-y-3"
               data-testid="deck-preview-finalized">
            <CheckCircle2 size={32} className="mx-auto" style={{ color: '#2D8B5E' }} />
            <div className="text-[14px] font-medium" style={{ color: '#1B2A4A' }}>
              Deck generated and downloaded
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DeckPreviewSection({
  section, index, busy, showInstruction, instructionText,
  setInstructionText, onEditSave, onRegenerate,
  onRegenerateWithInstruction, onShowInstruction, onCancelInstruction,
}) {
  const [draft, setDraft] = useState(section.narrative || '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setDraft(section.narrative || ''); }, [section.narrative]);

  const typeColor = TYPE_COLORS[section.type] || { bg: '#E5E1DC', text: '#1B2A4A' };

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        background: '#FFFEFB',
        border: '1px solid #E5E1DC',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}
      data-testid={`deck-section-${section.id}`}>
      {/* Section header */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: typeColor.bg, color: typeColor.text }}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider opacity-80">
            {String(index + 1).padStart(2, '0')} · {TYPE_LABELS[section.type] || section.type}
          </span>
          <ChevronRight size={12} className="opacity-60" />
          <span className="text-[13px] font-medium">{section.title}</span>
        </div>
        {section.editable && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={onRegenerate}
              disabled={busy}
              data-testid={`regen-${section.id}`}
              title="Ask Claude for a fresh rewrite (no instruction)"
              className="text-[10.5px] px-2 py-1 rounded flex items-center gap-1 bg-white/90 hover:bg-white disabled:opacity-60"
              style={{ color: '#1B2A4A' }}>
              {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              Rewrite
            </button>
            <button
              onClick={onShowInstruction}
              disabled={busy}
              data-testid={`regen-instruct-${section.id}`}
              title="Ask Claude to rewrite with a specific instruction"
              className="text-[10.5px] px-2 py-1 rounded flex items-center gap-1 bg-white/90 hover:bg-white disabled:opacity-60"
              style={{ color: '#1B2A4A' }}>
              <Wand2 size={10} /> Rewrite with instruction
            </button>
          </div>
        )}
      </div>

      {/* Instruction input */}
      {showInstruction && (
        <div className="px-4 py-3 border-b"
             style={{ background: '#FFF8EE', borderColor: '#E5E1DC' }}
             data-testid={`instruction-${section.id}`}>
          <label className="text-[10.5px] font-medium uppercase tracking-wider"
                 style={{ color: '#8A4B00' }}>
            Tell Claude how to rewrite this
          </label>
          <input
            type="text"
            autoFocus
            value={instructionText}
            onChange={(e) => setInstructionText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && instructionText.trim()) {
                onRegenerateWithInstruction();
              } else if (e.key === 'Escape') {
                onCancelInstruction();
              }
            }}
            placeholder='e.g. "Make it shorter" · "Mention Mumbai" · "More direct, less lyrical" · "Cut the closing paragraph"'
            className="mt-1.5 w-full text-[13px] px-2.5 py-1.5 rounded border"
            style={{ borderColor: '#D9D3CA', color: '#1B2A4A',
                      background: '#FFFFFF' }}
            data-testid={`instruction-input-${section.id}`}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={onRegenerateWithInstruction}
              disabled={busy || !instructionText.trim()}
              data-testid={`instruction-submit-${section.id}`}
              className="text-[11px] px-2.5 py-1 rounded flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
              style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
              {busy ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
              Rewrite now
            </button>
            <button
              onClick={onCancelInstruction}
              className="text-[11px] px-2.5 py-1 rounded hover:bg-white"
              style={{ color: '#6B7280' }}>
              Cancel
            </button>
            <span className="text-[10px] ml-auto" style={{ color: '#8A4B00' }}>
              Enter to rewrite · Esc to cancel
            </span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="px-5 py-4">
        {section.subtitle && (
          <div className="text-[10.5px] mb-2" style={{ color: '#6B7280' }}>
            {section.subtitle}
          </div>
        )}
        {section.editable ? (
          editing ? (
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => {
                setEditing(false);
                if (draft !== section.narrative) onEditSave(draft);
              }}
              rows={Math.max(6, Math.min(24, (draft || '').split('\n').length + 3))}
              className="w-full text-[13px] px-3 py-2 rounded border font-sans"
              style={{
                color: '#1B2A4A', borderColor: '#D9D3CA',
                background: '#FFFFFF', lineHeight: 1.6,
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}
              data-testid={`edit-${section.id}`}
            />
          ) : (
            <div
              onClick={() => setEditing(true)}
              className="text-[13px] cursor-text hover:bg-[rgba(255,248,238,0.5)] rounded -mx-1 px-1 py-0.5 transition-colors"
              style={{
                color: '#1B2A4A', lineHeight: 1.65, whiteSpace: 'pre-wrap',
                fontFamily: 'Georgia, "Times New Roman", serif',
              }}
              data-testid={`narrative-${section.id}`}
              title="Click to edit">
              {renderItalics(section.narrative) ||
                <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                  Claude returned no narrative for this section — click to write manually.
                </span>}
            </div>
          )
        ) : (
          <div className="text-[12px]" style={{ color: '#6B7280' }}>
            {section.preview_text || 'Auto-generated from data · not editable.'}
          </div>
        )}
        {section.editable && !editing && (
          <div className="mt-2 text-[10.5px] flex items-center gap-1"
               style={{ color: '#9CA3AF' }}>
            <Edit3 size={9} /> Click to edit inline
          </div>
        )}
        {section.last_instruction && (
          <div className="mt-2 text-[10px]" style={{ color: '#8A4B00' }}>
            Last rewrite instruction: <em>"{section.last_instruction}"</em>
          </div>
        )}
      </div>
    </div>
  );
}

// Convert *asterisk-wrapped* phrases to <em> tags for display
function renderItalics(text) {
  if (!text) return null;
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}
